import React from 'react';
import {
  makeStyles,
  Theme,
  Grid,
  List,
  Paper,
  Typography,
  Box
} from '@material-ui/core';
import {
  Result
} from '@backstage/plugin-search-common';
import {
  Content,
  Header,
  Page,
  Progress,
  EmptyState
} from '@backstage/core-components';
import {
  BackstagePlugin,
  useApi,
  useApp
} from '@backstage/core-plugin-api';
import {
  EpDevPEventApiProductsIcon,
  EpDevPEventsIcon,
  EpDevPConsumerProductsIcon,
  EpDevPConsumerAppsIcon,
  // EpDevPApplicationsIcon,
  // EpDevPEventApisIcon
} from "@solace-labs/backstage-plugin-ep-devp-react";
import {
  EpDevPUtils,
  IEpDevPListApplicationDomainsResponse,
  EpDevPEntityIdsService,
  IEpDevPApplicationDomain,
  IEpDevPManagedVersionedAssetIndexableDocument,
  EpDevPDocumentTypes,
  EpDevPPluralKinds,
  IEpDevPListEnvironmentsResponse,
  IEpDevPEnvironment,
  EpDevPSearchPageAssetFilters,
  IEpDevPManagedAssetIndexableDocument,
  TEpDevPEntityId,
  EpDevPComponentError,
  IEpDevPEnvironment4IndexableDocument
} from '@solace-labs/backstage-plugin-ep-devp-common';
import {
  epDevPApplicationDomainsApiClientRef,
  epDevPEnvironmentsApiClientRef,
  useEpDevPAppContext
} from '@solace-labs/backstage-plugin-ep-devp';
import {
  EpDevPResponseErrorPanel
} from '@solace-labs/backstage-plugin-ep-devp-react';
import {
  EpDevPSearchResultListItem
} from './EpDevPSearchResultListItem';
import {
  EpDevPSearchContextValue,
  useEpDevPSearch
} from '../context';
import {
  EpDevPSearchNotADocumentType,
  IEpDevPSearchListResponse
} from '@solace-labs/backstage-plugin-ep-devp-search-common';
import {
  EpDevPSearchDocumentType,
  EpDevPSearchDocumentTypesAccordion
} from './EPDevPSearchDocumentTypes';
import {
  EpDevPSearchFilterSelect,
  EpDevPSearchFilterCheckbox
} from './EpDevPSearchFilters';
import {
  EpDevPSearchBar
} from './EpDevPSearchBar';
import {
  EpDevPSearchPagination
} from './EpDevPSearchPagination';
import { useServiceRegistryTranslation } from '../../../../i18n/ServiceRegistryLanguageProvider';


const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
}));
export interface IEpDevPSearchPageOnSelectItemData {
  assetEntityId: TEpDevPEntityId;
  domainId: string;
}
export interface IEpDevPSearchPageSelectItem {
  onSelect: (epDevPSearchPageOnSelectItemData: IEpDevPSearchPageOnSelectItemData) => void;
  text: string;
}
export interface IEpDevPSearchPageControlProps {
  type?: string;
  title?: string;
  subtitle?: string;
  documentTypes?: Array<EpDevPDocumentTypes>;
  assetFilters?: Array<EpDevPSearchPageAssetFilters>;
  selectItem?: IEpDevPSearchPageSelectItem;
  searchBarPlaceholder?: string;
  excludeAssetIds?: Array<string>;
}

export const EpDevPSearchPageControl: React.FC<IEpDevPSearchPageControlProps> = (props: IEpDevPSearchPageControlProps) => {
  const { t } = useServiceRegistryTranslation();
  const ComponentName = 'EpDevPSearchPageControl';

  const classes = useStyles();
  const useSearchContext: EpDevPSearchContextValue = useEpDevPSearch();
  const applicationDomainsApi = useApi(epDevPApplicationDomainsApiClientRef);
  const environmentsApi = useApi(epDevPEnvironmentsApiClientRef);
  const app = useApp();
  const [epDevPAppContextState] = useEpDevPAppContext();
  const [documentTypes, setDocumentTypes] = React.useState<Array<EpDevPSearchDocumentType>>([]);
  const [isInitializedDocumentTypes, setIsInitializedDocumentTypes] = React.useState<boolean>(false);
  const [isInitializedFilters, setIsInitializedFilters] = React.useState<boolean>(false);
  const [eventPortalAssetFiltersComponent, setEventPortalAssetFiltersComponent] = React.useState<JSX.Element>();
  const [isInitializedEventPortalAssetFiltersComponent, setIsInitializedEventPortalAssetFiltersComponent] = React.useState<boolean>(false);
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [componentError, setComponentError] = React.useState<any>();
  const [domainSelectEntityIdList, setDomainSelectEntityIdList] = React.useState<Array<TEpDevPEntityId>>([]);
  const [environmentSelectEntityIdList, setEnvironmentSelectEntityIdList] = React.useState<Array<TEpDevPEntityId>>([]);
  const [isInitializedData, setIsInitializedData] = React.useState<boolean>(false);
  const apiGetDomainList = async () => {
    const funcName = 'apiGetDomainList';
    const logName = `${ComponentName}.${funcName}()`;
    
    try {
      const epDevPListApplicationDomainsResponse: IEpDevPListApplicationDomainsResponse = await applicationDomainsApi.listCachedApplicationDomains({
        organizationId: epDevPAppContextState.organizationId,
        documentTypes: props.documentTypes
      });
      setDomainSelectEntityIdList(
        EpDevPEntityIdsService.sortByDisplayName(EpDevPEntityIdsService.getEntityIdListFromEntityIdObjectList<IEpDevPApplicationDomain>(epDevPListApplicationDomainsResponse.list))
      );
    } catch (e: any) {
      setComponentError(new EpDevPComponentError(logName, "loading domains", e));
    }
  }

  const apiGetEnvironmentList = async () => {
    const funcName = 'apiGetEnvironmentList';
    const logName = `${ComponentName}.${funcName}()`;
    try {
      const epDevPListEnvironmentsResponse: IEpDevPListEnvironmentsResponse = await environmentsApi.listCachedEnvironments({
        organizationId: epDevPAppContextState.organizationId,
        documentTypes: props.documentTypes
      });
      setEnvironmentSelectEntityIdList(
        EpDevPEntityIdsService.sortByDisplayName(EpDevPEntityIdsService.getEntityIdListFromEntityIdObjectList<IEpDevPEnvironment>(epDevPListEnvironmentsResponse.list))
      );
    } catch (e: any) {
      setComponentError(new EpDevPComponentError(logName, "loading environments", e));
    }
  }

  const doInitializeData = async () => {
    const assetFilters: Array<EpDevPSearchPageAssetFilters> = props.assetFilters ? props.assetFilters : epDevPAppContextState.search.searchPage.assetFilters;
    if (assetFilters.includes(EpDevPSearchPageAssetFilters.Domains)) await apiGetDomainList();
    if (assetFilters.includes(EpDevPSearchPageAssetFilters.Environments)) await apiGetEnvironmentList();
    setIsInitializedData(true);
  }

  // use if we want to render an icon in the result list
  // const getDocumentTypeIcon = (documentType: EpDevPDocumentTypes): JSX.Element => {
  //   switch(documentType) {
  //     case EpDevPDocumentTypes.EpDevPConsumerAppsDocumentType:
  //       return (<EpDevPConsumerAppsIcon />);
  //     case EpDevPDocumentTypes.EpDevPConsumerProductsDocumentType:
  //       return (<EpDevPConsumerProductsIcon />);
  //     case EpDevPDocumentTypes.EpDevPEventApiProductsDocumentType:
  //       return (< EpDevPEventApiProductsIcon />);
  //     case EpDevPDocumentTypes.EpDevPEventsDocumentType:
  //       return (<EpDevPEventsIcon />);
  //     default:
  //       return (<></>);
  //   }
  // }

  const getDocumentType = (documentType: EpDevPDocumentTypes): EpDevPSearchDocumentType | undefined => {
    if (epDevPAppContextState.isPluginActive[documentType]) {
      switch (documentType) {
        case EpDevPDocumentTypes.EpDevPConsumerAppsDocumentType:
          return {
            id: EpDevPDocumentTypes.EpDevPConsumerAppsDocumentType as unknown as string,
            displayName: EpDevPPluralKinds.EpDevPConsumerAppsKindPlural,
            icon: <EpDevPConsumerAppsIcon />,
          };
        case EpDevPDocumentTypes.EpDevPConsumerProductsDocumentType:
          return {
            id: EpDevPDocumentTypes.EpDevPConsumerProductsDocumentType as unknown as string,
            displayName: EpDevPPluralKinds.EpDevPConsumerProductsKindPlural,
            icon: <EpDevPConsumerProductsIcon />,
          };
        case EpDevPDocumentTypes.EpDevPEventApiProductsDocumentType:
          return {
            id: EpDevPDocumentTypes.EpDevPEventApiProductsDocumentType as unknown as string,
            displayName: EpDevPPluralKinds.EpDevPEventApiProductsKindPlural,
            icon: < EpDevPEventApiProductsIcon />
          };
        case EpDevPDocumentTypes.EpDevPEventsDocumentType:
          return {
            id: EpDevPDocumentTypes.EpDevPEventsDocumentType as unknown as string,
            displayName: EpDevPPluralKinds.EpDevPEventsKindPlural,
            icon: <EpDevPEventsIcon />,
          };
        default:
          // // use the new error component
          // throw new Error(`${ComponentName}: no plugin installed for selected documentType=${documentType}`);
          return undefined;
      }
    } else {
      // // use the new error component
      // throw new Error(`${ComponentName}: no plugin installed for selected documentType=${documentType}`);
      return undefined;
    }
  }

  const initializeDocumentTypes = () => {
    const newDocumentTypes: Array<EpDevPSearchDocumentType> = [];
    if (props.documentTypes && props.documentTypes.length > 0) {
      props.documentTypes.forEach((documentType) => {
        const dt = getDocumentType(documentType);
        if (dt && epDevPAppContextState.isPluginActive[documentType]) newDocumentTypes.push(dt);
      });
    } else {
      const plugins: Array<BackstagePlugin> = app.getPlugins();
      for (const plugin of plugins) {
        const pluginId = plugin.getId();
        if (epDevPAppContextState.isPluginActive[pluginId]) {
          const documentType: EpDevPDocumentTypes = pluginId as unknown as EpDevPDocumentTypes;
          const dt = getDocumentType(documentType);
          if (dt) newDocumentTypes.push(dt);
        }
      }
    }
    // sort by name
    newDocumentTypes.sort((one, two) => {
      const _one: string = one.displayName.toLowerCase();
      const _two: string = two.displayName.toLowerCase();
      return _one.localeCompare(_two);
    });
    setDocumentTypes(newDocumentTypes);
  }

  const SearchDocumentTypeComponent = (): JSX.Element => {
    if (!isInitializedDocumentTypes) return (<></>);
    if (documentTypes.length < 2) return (<></>);
    return (
      <EpDevPSearchDocumentTypesAccordion
        // title="Event Portal Asset Type"
        documentTypes={documentTypes}
      />
    );
  }

  const SearchFilterDomains = () => {
    if (domainSelectEntityIdList.length === 0) return (<></>);
    return (
      <EpDevPSearchFilterSelect
        key="domain-filter"
        className={classes.filter}
        title={t('developerSearch.domains')}
        searchFieldName={EpDevPUtils.nameOf<IEpDevPManagedAssetIndexableDocument>("epDevPManagedAsset.epDevPDomainInfo.owningDomainEntityId.displayName")}
        valuesEntityIdList={domainSelectEntityIdList}
      />
    );
  }

  const SearchFilterState = () => {
    return (
      <EpDevPSearchFilterCheckbox
        className={classes.filter}
        title={t('developerSearch.state')}
        searchFieldName={EpDevPUtils.nameOf<IEpDevPManagedVersionedAssetIndexableDocument>("epDevPManagedAsset.lifecycleState")}
        valuesEntityIdList={[
          { id: 'Draft', displayName: t('developerSearch.draft') },
          { id: 'Released', displayName: t('developerSearch.released') },
          { id: 'Deprecated', displayName: t('developerSearch.deprecated') },
          { id: 'Retired', displayName: t('developerSearch.retired') },
        ]}
      />
    );
  }

  const SearchFilterBrokerType = () => {
    return (
      <EpDevPSearchFilterCheckbox
        className={classes.filter}
        title={t('developerSearch.serviceType')}
        searchFieldName={EpDevPUtils.nameOf<IEpDevPManagedAssetIndexableDocument>("epDevPManagedAsset.brokerType")}
        valuesEntityIdList={[
          { id: 'solace', displayName: 'solace' },
          { id: 'kafka', displayName: 'kafka' }
        ]}
      />
    );
  }

  const SearchFilterEnvironments = () => {
    return (
      <EpDevPSearchFilterCheckbox
        className={classes.filter}
        title={t('developerSearch.environments')}
        searchFieldName={EpDevPUtils.nameOf<IEpDevPEnvironment4IndexableDocument>("environmentIdList")}
        valuesEntityIdList={environmentSelectEntityIdList}
      />
    );
  }

  const initializeEventPortalAssetFilterComponent = () => {
    const funcName = 'initializeEventPortalAssetFilterComponent';
    const logName = `${ComponentName}.${funcName}()`;
    const componentList: Array<JSX.Element> = [];
    const assetFilters: Array<EpDevPSearchPageAssetFilters> = props.assetFilters ? props.assetFilters : epDevPAppContextState.search.searchPage.assetFilters;
    if (assetFilters.length > 0) {
      for (const assetFilter of assetFilters) {
        switch (assetFilter) {
          case EpDevPSearchPageAssetFilters.Domains:
            componentList.push(<SearchFilterDomains />);
            break;
          case EpDevPSearchPageAssetFilters.State:
            componentList.push(<SearchFilterState />);
            break;
          case EpDevPSearchPageAssetFilters.BrokerType:
            componentList.push(<SearchFilterBrokerType />)
            break;
          case EpDevPSearchPageAssetFilters.Environments:
            componentList.push(<SearchFilterEnvironments />);
            break;
          default:
            EpDevPUtils.assertNever(logName, assetFilter);
        }
      }
    }
    const assetFiltersComponent = (): JSX.Element => {
      const text = props.type ? t('developerSearch.filter') : t('developerSearch.filterAssets');
      return (
        <Paper className={classes.filters}>
          <Box marginBottom="10px" >
            <Typography>{text}</Typography>
          </Box>
          {componentList}
        </Paper>
      );
    }
    setEventPortalAssetFiltersComponent(assetFiltersComponent);
  }

  React.useEffect(() => {
    doInitializeData();
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  React.useEffect(() => {
    if (!isInitializedData) return;
    initializeDocumentTypes();
    initializeEventPortalAssetFilterComponent();
  }, [isInitializedData, app]); /* eslint-disable-line react-hooks/exhaustive-deps */

  React.useEffect(() => {
    if (!isInitializedData) return;
    setIsInitializedDocumentTypes(true);
  }, [isInitializedData, documentTypes]); /* eslint-disable-line react-hooks/exhaustive-deps */

  React.useEffect(() => {
    if (eventPortalAssetFiltersComponent === undefined) return;
    setIsInitializedEventPortalAssetFiltersComponent(true);
  }, [eventPortalAssetFiltersComponent]); /* eslint-disable-line react-hooks/exhaustive-deps */

  React.useEffect(() => {
    if (!epDevPAppContextState.isInitialized) return;
    if (!isInitializedDocumentTypes) return;
    useSearchContext.setOrganizationId(epDevPAppContextState.organizationId);
    if (documentTypes.length === 0) useSearchContext.setDocumentTypes([EpDevPSearchNotADocumentType]);
    else useSearchContext.setDocumentTypes(documentTypes.map((x) => { return x.id; }));
    useSearchContext.setExcludeAssetIds(props.excludeAssetIds);
    setIsInitializedFilters(true);
  }, [epDevPAppContextState, isInitializedDocumentTypes]); /* eslint-disable-line react-hooks/exhaustive-deps */

  React.useEffect(() => {
    if (!isInitializedDocumentTypes) return;
    if (!isInitializedFilters) return;
    if (!isInitializedEventPortalAssetFiltersComponent) return;
    setIsInitialized(true);
  }, [isInitializedDocumentTypes, isInitializedFilters, isInitializedEventPortalAssetFiltersComponent]); /* eslint-disable-line react-hooks/exhaustive-deps */


  const renderSearchResult = () => {
    const { value, error, loading } = useSearchContext.result;
    if (loading) return (<Progress />);
    if (error) return (
      <EpDevPResponseErrorPanel
        componentName={ComponentName}
        location={window.location.href}
        organizationId={epDevPAppContextState.organizationId}
        title={t('developerSearch.searchError')}
        error={error}
        subject={t('developerSearch.searchError')}
        defaultExpanded={false}
      />
    );
    if (value) {
      const epDevPSearchListResponse: IEpDevPSearchListResponse = value;
      
      if (epDevPSearchListResponse.list.length === 0) return (
        <EmptyState missing="data" title={t('developerSearch.noResults')} />
      )
      return (
        <List>
          {
            epDevPSearchListResponse.list.map((result: Result<IEpDevPManagedAssetIndexableDocument>) => {
              return (
                <EpDevPSearchResultListItem
                  key={result.document.location}
                  result={result.document}
                  highlight={result.highlight}
                  rank={result.rank}
                  selectItem={props.selectItem}
                // icon={getDocumentTypeIcon(result.type as unknown as EpDevPDocumentTypes)}
                />
              );
            })
          }
        </List>
      )
    }
    // should we display an error instead?
    return (<EmptyState missing="data" title={t('developerSearch.noResults')} />);
  }

  if (!isInitialized) return (
    <Progress />
  );
  if (componentError) return (
    <EpDevPResponseErrorPanel
      componentName={ComponentName}
      location={window.location.href}
      organizationId={epDevPAppContextState.organizationId}
      title={t('developerSearch.searchError')}
      error={componentError}
      subject={t('developerSearch.searchError')}
      defaultExpanded={false}
    />
  );
  return (
    <Page themeId="home">
      <Header
        type={props.type}
        title={props.title ? props.title : t('developerSearch.catalogSearch')}
        subtitle={props.subtitle}
      />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <Paper className={classes.bar}>
              <EpDevPSearchBar
                placeholder={props.searchBarPlaceholder ? props.searchBarPlaceholder : props.title}
              />
            </Paper>
          </Grid>

          <Grid item xs={3}>
            <SearchDocumentTypeComponent />
            {eventPortalAssetFiltersComponent}
          </Grid>

          <Grid item xs={9}>
            < EpDevPSearchPagination />
            {renderSearchResult()}
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
